import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { HStack, VStack } from "../../../../components/utils";
import { Button, ChevronDown, Search, X } from "./ComponentUtils";
import { Input } from "../../../../components/shared/InputField";

const ExcludedPersonnel = ({
  formik,
  options,
  disableUpdate = false,
}: {
  formik: any;
  options: { name: string; id: string }[];
  disableUpdate?: boolean;
}) => {
  const [searchValue, setSearchValue] = useState<string>("");

  // Filter options based on search input
  let filteredOptions = options.filter((option) =>
    option.name.toLowerCase()?.includes(searchValue.toLowerCase())
  );

  // Check if an employee is excluded
  const isExcluded = (id: string) =>
    formik.values.offerFilter.excludeEmployeeIds
      ?.map((e: any) => e.id || e.employeeId)
      .includes(id);

  if (disableUpdate) {
    filteredOptions = filteredOptions.filter((option) => isExcluded(option.id));
  }

  // Handle adding/removing an employee by ID
  const toggleExcludeEmployee = (option: { name: string; id: string }) => {
    if (disableUpdate) return;
    const updatedIds = isExcluded(option.id)
      ? formik.values.offerFilter.excludeEmployeeIds.filter(
          (excludeId: { name: string; id: string }) =>
            excludeId.id !== option.id
        )
      : [...formik.values.offerFilter.excludeEmployeeIds, option];

    formik.setFieldValue("offerFilter.excludeEmployeeIds", updatedIds);
  };

  return (
    <Popover
      content={
        <PopoverContent className="w-80">
          <VStack className="gap-3">
            <div className="text-sm font-medium">Excluded Personnel</div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search by name"
                className="pl-10"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>

            {/* Display filtered employee options */}
            <VStack className="gap-2 overflow-y-auto max-h-60">
              {filteredOptions.map((option) => (
                <HStack
                  key={option.id}
                  className="justify-between px-3 py-2 border rounded-md"
                >
                  <span>{option.name}</span>
                  {isExcluded(option.id) ? (
                    <button
                      className="text-gray-500"
                      onClick={() => toggleExcludeEmployee(option)}
                    >
                      ✕
                    </button>
                  ) : (
                    <button
                      className="text-blue-500"
                      onClick={() => toggleExcludeEmployee(option)}
                    >
                      Exclude
                    </button>
                  )}
                </HStack>
              ))}
            </VStack>

            {/* Clear all excluded employees */}
            {!disableUpdate && (
              <HStack className="justify-between pt-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (disableUpdate) return;
                    formik.setFieldValue("offerFilter.excludeEmployeeIds", []);
                  }}
                >
                  Clear All
                </Button>
                <Button
                  variant="primary"
                  className="text-white bg-orange-500 hover:bg-orange-600"
                >
                  Apply
                </Button>
              </HStack>
            )}
          </VStack>
        </PopoverContent>
      }
    >
      <PopoverTrigger>
        <Button
          variant="outline"
          className={`flex items-center gap-2 ${
            formik.values.offerFilter.excludeEmployeeIds?.length > 0
              ? "bg-[#003366] text-white"
              : ""
          }`}
        >
          Excluded Personnel
          {formik.values.offerFilter.excludeEmployeeIds?.length > 0 && (
            <span className="bg-white text-[#003366] rounded-full w-6 h-6 flex items-center justify-center">
              {formik.values.offerFilter.excludeEmployeeIds?.length}
            </span>
          )}
          {formik.values.offerFilter.excludeEmployeeIds?.length > 0 &&
          !disableUpdate ? (
            <X
              className="w-4 h-4 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                formik.setFieldValue("offerFilter.excludeEmployeeIds", []);
              }}
            />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </PopoverTrigger>
    </Popover>
  );
};

export default ExcludedPersonnel;
